import { BoldItalicUnderlineToggles, headingsPlugin, listsPlugin, ListsToggle, MDXEditor, type MDXEditorMethods, quotePlugin, toolbarPlugin, UndoRedo, BlockTypeSelect, Separator } from "@mdxeditor/editor";
import { FC, useEffect } from "react";
import "@mdxeditor/editor/style.css";
import "./MdxEditorComponent.css";
import { Match } from "../../../../hooks/useFindReplace";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { useAppDispatch } from "../../../../redux/useAppDispatch";
import { updateBlock } from "../../../../redux/JobModule/QCDetailPage/QCDetailPageAction";
import { eliminateLinesAndSymbols } from "../../../../utils/eliminateInvalidLinesInDictationFile";

interface EditorProps {
  markdown: string;
  editorRef?: React.MutableRefObject<MDXEditorMethods | null>;
  disabled: boolean;
  allMatches: Match[];
  currentMatchIndex: number;
  packId: number;
  texts: any[];
}

/**
 * Extend this Component further with the necessary plugins or props you need.
 * proxying the ref is necessary. Next.js dynamically imported components don't support refs.
 */
const Editor: FC<EditorProps> = ({ markdown, editorRef, disabled, allMatches, currentMatchIndex, texts, packId }) => {
  const idTranscription = useSelector((state: RootState) => state.qCDetailPageState.data.transcriptions[0].idTranscription);
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (editorRef?.current) {
      let cleanDictation = editorRef.current
        .getMarkdown()
        .replace(/<span class="highlighted">/g, "")
        .replace(/<\/span>/g, "");
      cleanDictation = eliminateLinesAndSymbols(cleanDictation);
      if (allMatches.length === 0) {
        editorRef.current.setMarkdown(cleanDictation);
        return;
      }

      const match = allMatches[currentMatchIndex];

      const startSubstring = cleanDictation.substring(0, match.start);
      const endSubstring = cleanDictation.substring(match.end);
      const realSearchSubstring = cleanDictation.substring(match.start, match.end);
      const replaceText = startSubstring + `<span class="highlighted">${realSearchSubstring}</span>` + endSubstring;
      editorRef.current.setMarkdown(replaceText);
    }
  }, [allMatches, currentMatchIndex, editorRef, markdown]);

  // Clean the markdown for UI display
  const cleanedMarkdown = eliminateLinesAndSymbols(markdown);
  return (
    <div className="d-flex flex-column overflow-hidden">
      <div className="markdownEditor ">
        <MDXEditor
          ref={editorRef}
          markdown={cleanedMarkdown}
          readOnly={disabled}
          contentEditableClassName="prose"
          plugins={[
            headingsPlugin(),
            listsPlugin(),
            quotePlugin(),

            toolbarPlugin({
              toolbarContents: () => (
                <>
                  {" "}
                  <UndoRedo /> <Separator /> <BoldItalicUnderlineToggles />
                  <Separator />
                  <ListsToggle /> <Separator />
                  <BlockTypeSelect />
                </>
              ),
            }),
          ]}
          //   onBlur={(event) => onBlur(event)}
        />
      </div>
      <div className="d-flex w-100">
        <button
          className="button"
          style={{ padding: "10px 20px", marginBottom: "5px", alignSelf: "flex-end" }}
          disabled={disabled}
          onClick={() => {
            if (editorRef?.current) {
              const newBlock = {
                TranscriptionId: idTranscription,
                TranscriptionBlocks: [
                  {
                    TranscriptionBlockId: texts[0].idTranscriptionBlock,
                    StartTimeSec: texts[0].startTimeSec,
                    EndTimeSec: texts[0].endTimeSec,
                    Speaker: texts[0].speaker,
                    Text: eliminateLinesAndSymbols(
                      editorRef.current
                        .getMarkdown()
                        .replace(/<span class="highlighted">/g, "")
                        .replace(/<\/span>/g, "")
                    ),
                  },
                ],
              };
              dispatch(
                updateBlock({
                  id: idTranscription,
                  data: newBlock,
                  blockIndex: 0,
                })
              );
            } else {
              console.error("editorRef.current is undefined");
            }
          }}>
          Save
        </button>
      </div>
    </div>
  );
};

export default Editor;
